import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AuthCallback from './components/AuthCallback';
import InstallAppBanner from './components/InstallAppBanner';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ParentDashboard from './pages/ParentDashboard';
import CoachDashboard from './pages/CoachDashboard';
import AdminDashboard from './pages/AdminDashboard';
import MyFamilyPage from './pages/FamilyPage';
import SchedulePage from './pages/SchedulePage';
import FundraisingPage from './pages/FundraisingPage';
import CommunityPage from './pages/CommunityPage';
import CommunityContent from './pages/CommunityContent';
import CommunicationsHub from './pages/CommunicationsHub';
import RegistrationWizard from './pages/RegistrationWizard';
import AdminRegistrationsList from './pages/AdminRegistrationsList';
// Step 4 & 5 imports
import AdminTeamsPage from './pages/AdminTeamsPage';
import AdminTeamDetail from './pages/AdminTeamDetail';
import AdminGallery from './pages/AdminGallery';
import CoachTeamPage from './pages/CoachTeamPage';
import EventDetailPage from './pages/EventDetailPage';
// Step 6 imports
import AnnouncementDetailPage from './pages/AnnouncementDetailPage';
import MessageThreadPage from './pages/MessageThreadPage';
import CampaignDetailPage from './pages/CampaignDetailPage';
// Step 7 imports
import VolunteerPage from './pages/VolunteerPage';
import AdminModerationPage from './pages/AdminModerationPage';
import AdminAnnouncementsPage from './pages/AdminAnnouncementsPage';
import AdminVolunteerPage from './pages/AdminVolunteerPage';
import AdminVisibilityControls from './pages/AdminVisibilityControls';
import StaffDirectoryPage from './pages/StaffDirectoryPage';
import AdminStaffPage from './pages/AdminStaffPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminPlayerReportPage from './pages/AdminPlayerReportPage';
import AdminPaymentsPage from './pages/AdminPaymentsPage';
import AdminSpotlightsPage from './pages/AdminSpotlightsPage';
import AdminSponsorsPage from './pages/AdminSponsorsPage';
import AdminProgramConfigPage from './pages/AdminProgramConfigPage';
import AdminRosterNotesPage from './pages/AdminRosterNotesPage';
import AdminAgeMismatchPage from './pages/AdminAgeMismatchPage';
import AdminSmsStatusPage from './pages/AdminSmsStatusPage';
// Coach Application imports
import CoachApplicationPage from './pages/CoachApplicationPage';
import CoachApplicationStatusPage from './pages/CoachApplicationStatusPage';
import AdminCoachApplicationsPage from './pages/AdminCoachApplicationsPage';
// Finance imports
import AdminFinancesPage from './pages/AdminFinancesPage';
// Event imports
import AdminEventCreatePage from './pages/AdminEventCreatePage';
// Background Check imports
import BackgroundCheckPage from './pages/BackgroundCheckPage';
// Accountability imports
import TeacherFeedbackPage from './pages/TeacherFeedbackPage';
import AdminAccountabilityPage from './pages/AdminAccountabilityPage';
import AdminAcademicsPage from './pages/AdminAcademicsPage';
import CoverPage from './pages/CoverPage';
import GuestPage from './pages/GuestPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ParentLayout from './components/ParentLayout';
import '@/App.css';

const CommunityFullPage = () => (
  <ParentLayout>
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="community-full-page">
      <div className="mb-4">
        <h1 className="font-display text-3xl sm:text-4xl font-black text-navy uppercase tracking-tighter">Community</h1>
        <p className="text-sm text-gray-500 mt-1">Connect with the Park City Chiefs family</p>
      </div>
      <CommunityContent />
    </div>
  </ParentLayout>
);

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id BEFORE rendering routes
  // This prevents race conditions by processing OAuth callback synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      
      {/* Teacher Feedback - Public route (no auth required) */}
      <Route path="/feedback/:token" element={<TeacherFeedbackPage />} />
      
      {/* Guest Page - Public route */}
      <Route path="/guest" element={<GuestPage />} />

      {/* Terms + Privacy Policy — public, referenced from Twilio SMS opt-in card */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      
      {/* Coach Application - Public routes */}
      <Route path="/coach-application" element={<CoachApplicationPage />} />
      <Route path="/coach-application/status" element={<CoachApplicationStatusPage />} />
      
      {/* Parent routes (also accessible by coaches who are parents) */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['parent', 'coach', 'team_staff', 'team_mom', 'super_admin', 'league_admin']}>
            <ParentDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/family"
        element={
          <ProtectedRoute allowedRoles={['parent', 'coach', 'team_staff', 'team_mom', 'super_admin', 'league_admin']}>
            <MyFamilyPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/registration/:registrationId"
        element={
          <ProtectedRoute allowedRoles={['parent', 'coach', 'team_staff', 'team_mom', 'super_admin', 'league_admin']}>
            <RegistrationWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <ProtectedRoute allowedRoles={['parent', 'coach', 'team_staff', 'team_mom', 'super_admin', 'league_admin']}>
            <SchedulePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={<Navigate to="/communications?tab=messages" replace />}
      />
      <Route
        path="/dm"
        element={<Navigate to="/communications?tab=messages" replace />}
      />
      <Route
        path="/communications"
        element={
          <ProtectedRoute allowedRoles={['parent', 'coach', 'team_staff', 'team_mom', 'super_admin', 'league_admin']}>
            <CommunicationsHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fundraising"
        element={
          <ProtectedRoute allowedRoles={['parent', 'coach', 'team_staff', 'team_mom', 'super_admin', 'league_admin']}>
            <FundraisingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/community"
        element={
          <ProtectedRoute allowedRoles={['parent', 'coach', 'team_staff', 'team_mom', 'super_admin', 'league_admin']}>
            <CommunityFullPage />
          </ProtectedRoute>
        }
      />
      
      {/* Coach routes */}
      <Route
        path="/coach/dashboard"
        element={
          <ProtectedRoute allowedRoles={['coach', 'team_staff', 'team_mom', 'league_admin', 'super_admin']}>
            <CoachDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Admin routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/registrations"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminRegistrationsList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/program-config"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminProgramConfigPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/roster-notes"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminRosterNotesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/age-mismatch"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminAgeMismatchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sms-status"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminSmsStatusPage />
          </ProtectedRoute>
        }
      />
      
      {/* Step 4: Admin Team Management */}
      <Route
        path="/admin/teams"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminTeamsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/teams/:teamId"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminTeamDetail />
          </ProtectedRoute>
        }
      />
      
      {/* Step 4: Coach Team View */}
      <Route
        path="/coach/team"
        element={
          <ProtectedRoute allowedRoles={['coach', 'team_staff', 'team_mom', 'league_admin', 'super_admin']}>
            <CoachTeamPage />
          </ProtectedRoute>
        }
      />
      
      {/* Step 5: Event Detail */}
      <Route
        path="/events/:eventId"
        element={
          <ProtectedRoute allowedRoles={['parent', 'coach', 'team_staff', 'team_mom', 'league_admin', 'super_admin']}>
            <EventDetailPage />
          </ProtectedRoute>
        }
      />
      
      {/* Admin: Create Event */}
      <Route
        path="/admin/events/new"
        element={
          <ProtectedRoute allowedRoles={['league_admin', 'super_admin', 'treasurer', 'coach', 'team_staff', 'team_mom']}>
            <AdminEventCreatePage />
          </ProtectedRoute>
        }
      />
      {/* Admin: Edit Event */}
      <Route
        path="/admin/events/:eventId/edit"
        element={
          <ProtectedRoute allowedRoles={['league_admin', 'super_admin', 'treasurer', 'coach', 'team_staff', 'team_mom']}>
            <AdminEventCreatePage />
          </ProtectedRoute>
        }
      />
      
      {/* Step 6: Communications */}
      <Route
        path="/messages/announcements/:announcementId"
        element={
          <ProtectedRoute allowedRoles={['parent', 'coach', 'team_staff', 'team_mom', 'league_admin', 'super_admin']}>
            <AnnouncementDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/threads/:threadId"
        element={
          <ProtectedRoute allowedRoles={['parent', 'coach', 'team_staff', 'team_mom', 'league_admin', 'super_admin']}>
            <MessageThreadPage />
          </ProtectedRoute>
        }
      />
      
      {/* Step 6: Fundraising */}
      <Route
        path="/fundraising/:campaignId"
        element={
          <ProtectedRoute allowedRoles={['parent', 'coach', 'team_staff', 'team_mom', 'league_admin', 'super_admin']}>
            <CampaignDetailPage />
          </ProtectedRoute>
        }
      />
      
      {/* Step 7: Community & Volunteer */}
      <Route
        path="/volunteer"
        element={
          <ProtectedRoute allowedRoles={['parent', 'coach', 'team_staff', 'team_mom', 'league_admin', 'super_admin']}>
            <VolunteerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/community"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminModerationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/announcements"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminAnnouncementsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/gallery"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminGallery />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/volunteers"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminVolunteerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/visibility"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminVisibilityControls />
          </ProtectedRoute>
        }
      />
      
      {/* Staff Directory */}
      <Route
        path="/directory"
        element={
          <ProtectedRoute allowedRoles={['parent', 'coach', 'team_staff', 'team_mom', 'league_admin', 'super_admin']}>
            <StaffDirectoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/staff"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminStaffPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/players/:playerId/report"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminPlayerReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payments"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'treasurer']}>
            <AdminPaymentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/spotlights"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminSpotlightsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sponsors"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminSponsorsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/coach-applications"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminCoachApplicationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/background-check/:applicationId?"
        element={
          <ProtectedRoute allowedRoles={['parent', 'coach', 'team_staff', 'team_mom', 'league_admin', 'super_admin']}>
            <BackgroundCheckPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/finances"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'treasurer']}>
            <AdminFinancesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/accountability"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminAccountabilityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/academics"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'league_admin', 'treasurer']}>
            <AdminAcademicsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Default redirect */}
      <Route path="/" element={<CoverPage />} />
      <Route path="/home" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <InstallAppBanner />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
